import { useEffect } from 'react';
import { get } from 'lodash';
import { amplitudeModule } from './AmplitudeModule';
import { initializeAmplitude, type InitializeAmplitudeOptions } from './initializeAmplitude';
import { getPlatformType } from './getPlatformType';
import { usePrescriptionStore } from '../../stores/prescriptionStore';

/**
 * Hook para inicializar o Amplitude na raiz da aplicação.
 * Configura o SDK, Session Replay e seta `platform_type` automaticamente.
 *
 * A inicialização é controlada pela feature flag `Features.enableAmplitude`
 * presente no `prescriberConfiguration`. Enquanto a flag não estiver disponível
 * ou for `false`, o SDK não será carregado e nenhum evento será enviado.
 *
 * @example
 * ```tsx
 * function App() {
 *   useAmplitude();
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAmplitude(options?: InitializeAmplitudeOptions): void {
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);

  useEffect(() => {
    const isAmplitudeEnabled = get(prescriberConfiguration, 'Features.enableAmplitude', false);

    if (!isAmplitudeEnabled || amplitudeModule.isInitialized()) return;

    const enableAutocapture = get(prescriberConfiguration, 'Features.enableAmplitudeAutocapture', false);
    const enableSessionReplay = get(prescriberConfiguration, 'Features.enableAmplitudeSessionReplay', false);
    const enableExperiment = get(prescriberConfiguration, 'Features.enableAmplitudeExperiment', false);

    const success = initializeAmplitude({
      ...options,
      autocapture: enableAutocapture,
      enableSessionReplay,
      sessionReplaySampleRate: 0.01,
      enableExperiment,
    });

    if (success) {
      amplitudeModule.setUserProperty('platform_type', getPlatformType());
    }
  }, [prescriberConfiguration]); // eslint-disable-line react-hooks/exhaustive-deps
}
